import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HertfordshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hertfordshire Pothole Claims | 38,806 Potholes Filled in One Year | Fixtyer"
        description="Hertfordshire County Council filled 38,806 potholes in 2024/25 — up 49% in five years. RED-condition B roads more than doubled in a year, and 18% of unclassified roads need maintenance despite a £29m improvement programme. See what the DfT AMBER rating means for your pothole claim."
        canonical="/councils/hertfordshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hertfordshire', url: '/councils/hertfordshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Hertfordshire spends three times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN — Hertfordshire spent £85.2m of capital in 2024/25 against a £25.7m DfT allocation. But the overall rating is AMBER because of road condition: RED-condition B roads more than doubled in a year and 18% of unclassified roads needed maintenance at the last published survey. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spending.',
          },
          {
            question: 'What if my pothole was on an unclassified or residential road?',
            answer: 'Unclassified roads make up 3,244km — nearly two-thirds of the Hertfordshire network. At the last published survey (2023/24), 18% were in the RED category, the worst figure since 2016/17 and worse than the 16% start point of the council\'s own £29m five-year improvement programme. The council also notes U-road figures come from "a more subjective visual inspection" rather than machine surveys.',
          },
          {
            question: 'RED-condition B roads doubled in a year — what does that mean for my claim?',
            answer: 'In 2024/25, B roads in the RED category jumped from 3.2% to 7.0% — the worst figure in the six years of published data — while B roads in good condition fell from 74.4% to 62.0%. If your pothole was on a B road, the council\'s own machine-survey data documents rapid, recent deterioration across that road class, which is directly relevant to whether its maintenance response was reasonable.',
          },
          {
            question: 'Hertfordshire says it fixes urgent potholes within 1 to 4 weeks — does that defeat my claim?',
            answer: 'No. That commitment applies only to defects already identified and risk-assessed as urgent Category 1 potholes. The real questions are whether your pothole was identified at all before your incident, whether it was risk-assessed correctly, and how long it sat below the "urgent" threshold. The council\'s own report says a shallow pothole on a low-use rural road "will not be such a high priority" — and lower-priority defects built up such a backlog that extra funding was allocated in 2023/24 to deal with them.',
          },
          {
            question: 'Does the "evolved roads" weather admission help my claim?',
            answer: 'Yes. Hertfordshire\'s transparency report states that much of its unclassified network is "evolved" — historic tracks made up over the years rather than designed roads — and that "the foundations of undesigned roads are much more susceptible to damage if moisture is allowed to penetrate through the surface." That is documented knowledge that a known class of roads deteriorates faster, which raises the bar for what a reasonable inspection and repair regime looks like on those roads under Section 58.',
          },
          {
            question: 'Why did pothole repairs jump 49% in five years?',
            answer: 'Hertfordshire filled 26,037 potholes in 2020/21 and 38,806 in 2024/25 — a 49% increase, averaging 106 repairs every day in the latest year. The council explains part of the jump itself: in 2023/24 it "allocated additional funding to address more carriageway defects... directed towards lower priority defects that did not meet assessment criteria for an urgent Cat1 pothole repair". In plain terms: there was a known backlog of sub-urgent defects on the network large enough to need its own funding line.',
          },
        ]}
      />
      <ServiceSchema 
        councilName="Hertfordshire County Council" 
        councilSlug="hertfordshire" 
        rating="amber" 
      />
      <SpeakableSchema
        url="/councils/hertfordshire"
        headline="Hertfordshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div 
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hertfordshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>
          
          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Hertfordshire: 38,806 Potholes a Year and a £29m Target in Reverse
          </h1>
          
          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hertfordshire County Council spends roughly three times its DfT capital allocation and earns a 
            GREEN scorecard for spend. Yet the overall rating is <strong className="text-amber-400">AMBER</strong> — 
            because RED-condition B roads <strong className="text-rose-400">more than doubled in a single year</strong>, 
            18% of unclassified roads needed maintenance at the last published survey despite a £29m improvement 
            programme, and pothole repairs are up 49% in five years.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">38,806</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2024/25 alone</div>
            <div className="text-brand-300 text-sm mt-1">
              Roughly 106 potholes every single day across Hertfordshire's 5,100km network — 
              up from 26,037 repairs in 2020/21, a 49% increase in five years.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Six years of survey data from Hertfordshire's own transparency report — 
            A roads holding steady, B roads falling off a cliff
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (697km — 14% of network): stable</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">3.0%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">was 3.0% in 2019/20 too</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">21.0%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">broadly flat across six years</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">76.0%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from a 79.3% peak</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Credit where due: main roads are machine-surveyed every year and have held steady. 
              But A roads are only one-seventh of the network.
            </p>
          </div>

          {/* B roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B roads: RED more than doubled in one year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2019/20</td>
                    <td className="px-4 py-2 text-center">3.5%</td>
                    <td className="px-4 py-2 text-center">22.3%</td>
                    <td className="px-4 py-2 text-center">74.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">6.3%</td>
                    <td className="px-4 py-2 text-center">27.7%</td>
                    <td className="px-4 py-2 text-center">66.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">19.6%</td>
                    <td className="px-4 py-2 text-center">77.7%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">3.0%</td>
                    <td className="px-4 py-2 text-center">21.0%</td>
                    <td className="px-4 py-2 text-center">76.0%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">3.2%</td>
                    <td className="px-4 py-2 text-center">22.4%</td>
                    <td className="px-4 py-2 text-center">74.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.0%</td>
                    <td className="px-4 py-2 text-center font-bold">31.0%</td>
                    <td className="px-4 py-2 text-center font-bold">62.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              In a single year, RED-condition B roads jumped from 3.2% to 7.0% — the worst figure in six years 
              of published data — and good-condition B roads collapsed from 74.4% to 62.0%. These are machine-survey 
              figures, not estimates. Over a third of the B road network now needs, or will soon need, maintenance.
            </p>
          </div>

          {/* C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">C roads: drifting back to where they started</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">6.0%</div>
                <div className="text-sm text-rose-800">RED (2024/25)</div>
                <div className="text-xs text-surface-500">up from 4.5% in 2021/22</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">29.0%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">highest in the six-year series</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">65.0%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">down from 70.5% in 2021/22</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              C roads improved to 4.5% RED in 2021/22, then gave the gains back: by 2024/25 they were at 6.0% RED — 
              level with 2019/20 — and 35% of the C network was RED or amber combined.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£25.7m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£85.2m</div>
                <div className="text-brand-300 text-sm">Total capital spend 2024/25</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£44.2m</div>
                <div className="text-brand-300 text-sm">Revenue spend on top 2024/25</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Hertfordshire spent over <span className="text-emerald-400">three times its DfT allocation</span> in 
              2024/25, and projects £90.9m of capital spend for 2025/26 — yet B and C roads are still declining 
              and pothole repairs keep rising. The problem isn't the chequebook. The network is deteriorating 
              faster than even a GREEN-rated spend programme can hold back.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Reversal */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The £29m Programme That Went Backwards
          </h2>
          <p className="text-surface-600 mb-8">
            64% of the network is unclassified roads — and they're now in worse shape than 
            before the council's five-year improvement programme began
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U roads in the RED category</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2016/17 <span className="text-surface-400 text-xs">(£29m programme start point)</span></td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2017/18</td>
                  <td className="px-6 py-4 text-center">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2018/19</td>
                  <td className="px-6 py-4 text-center">9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2019/20</td>
                  <td className="px-6 py-4 text-center">7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23 <span className="text-surface-400 text-xs">(target was 8%)</span></td>
                  <td className="px-6 py-4 text-center font-medium">10%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24 <span className="text-surface-400 text-xs">(latest published)</span></td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Worse Than Where It Started</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hertfordshire committed "an additional £29m over five years to improve the unclassified network" 
                with "the ambition to halve the percentage of roads that should be considered for maintenance" — 
                from 16% in 2016/17 to a target of 8% by 2022/23.
              </p>
              <p className="text-surface-600 text-sm">
                The actual 2022/23 figure was 10% — target missed. A year later it hit <strong>18% — higher 
                than the figure the £29m programme set out to halve</strong>. On a 3,244km unclassified network, 
                that is roughly 580km of residential streets, estate roads and rural lanes in the 
                "maintenance should be considered" category.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Subjective-Survey Caveat</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unlike A, B and C roads, which get machine surveys, the council's own report says U-road figures 
                "are usually based on a more subjective visual inspection by accredited inspectors" and "show 
                greater change from year to year".
              </p>
              <p className="text-amber-800 text-sm">
                To its credit, Hertfordshire surveys all unclassified roads every year — many councils don't. 
                But the council itself flags that the method is subjective, and it plans to phase out visual 
                inspections for automated technology under new DfT guidance. The condition record for two-thirds 
                of the network rests on the weakest survey method available.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing 
              the condition of its roads and acting on it. For Hertfordshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• If 18% of U roads were RED at the last published survey, what was the plan for yours?</li>
              <li>• The council knew the 8% target was missed in 2022/23 — what changed in response?</li>
              <li>• How reliable is a "subjective visual inspection" as the sole condition record for 3,244km of road?</li>
              <li>• If condition more than doubled in a year (7% to 18% between 2021/22 and 2023/24), were inspection frequencies increased to match?</li>
            </ul>
            <p className="text-white font-medium">
              A council that watched its unclassified network deteriorate past its own programme's start point 
              cannot easily argue the deterioration took it by surprise.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            155,452 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many potholes this network produces — 
            and the trend is sharply upwards
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated pothole repairs</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Per day</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">26,037</td>
                  <td className="px-6 py-4 text-center">~71</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">26,560</td>
                  <td className="px-6 py-4 text-center">~73</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">26,195</td>
                  <td className="px-6 py-4 text-center">~72</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">37,854</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~104</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">38,806</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">~106</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">155,452</td>
                  <td className="px-6 py-4 text-center font-bold">~85</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">A 46% Step Change in 2023/24</h3>
              <p className="text-surface-600 text-sm">
                Repairs ran at a steady ~26,000 a year for three years, then jumped 46% in 2023/24 and stayed 
                there. A network producing well over a hundred pothole repairs a day is, by definition, a network 
                where defects routinely form between inspections — exactly the scenario where prior reports and 
                photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Backlog Admission</h3>
              <p className="text-amber-800 text-sm">
                The council's own explanation for the jump: "In 2023/24 we allocated additional funding to address 
                more carriageway defects. It was directed towards lower priority defects that did not meet 
                assessment criteria for an urgent Cat1 pothole repair." In plain terms: there was a known backlog 
                of sub-urgent defects sitting on the network — <strong>large enough to need its own funding line</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Weather and Evolved Roads Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The "Evolved Roads" Admission
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for the U-road collapse — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="The increase in the score for the number of unclassified roads requiring treatment in 2023/24 was due to the weather which has significant impacts on the road network. Extreme heat, cold, precipitation has significantly damaged the unclassified road network."
              source="Hertfordshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="Much of the unclassified road network is referred to as 'evolved' where it could have been a historic track that has been made up over a number of years, rather than being a 'designed' road. The foundations of undesigned roads are much more susceptible to damage if moisture is allowed to penetrate through the surface."
              source="Hertfordshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
            <QuoteCard 
              quote="Changes in our weather patterns are producing more extreme peaks and prolonged periods of high temperature, wet weather and other factors that can contribute to and accelerate carriageway deterioration. The composition of many of our carriageways was not designed to cope with these conditions."
              source="Hertfordshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Hertfordshire formally acknowledges that a large part of its unclassified network sits on 
                undesigned foundations that fail faster when water gets in, and that today's weather is 
                accelerating deterioration across the carriageway stock generally.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerated, weather-driven deterioration on a known-vulnerable road class raises 
                the standard for what a "reasonable" inspection and repair regime looks like on those roads 
                under Section 58 — particularly after wet or freezing spells, when potholes form fastest.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li>• Was your road one of the "evolved" undesigned roads the council describes?</li>
                <li>• Did inspection frequency increase after the severe weather the council blames?</li>
                <li>• If the network was known to be "significantly damaged" in 2023/24, why wasn't your defect caught?</li>
                <li>• Weather explains deterioration — it doesn't excuse leaving a reported defect unrepaired.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The 1-4 Week Window */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1-to-4-Week Repair Window
          </h2>
          <p className="text-surface-600 mb-8">
            Hertfordshire's repair commitments are genuinely better than many councils' — 
            but read the small print
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard 
              quote="In Hertfordshire we aim to carry out a permanent repair to urgent potholes, cutting out the defective material and replacing with new within 1 to 4 weeks of the defect being identified, rather than carrying out a temporary fix that may need repeating. Over 90% of our urgent potholes are fixed with a first-time repair using hot material."
              source="Hertfordshire County Council Local Highways Maintenance Transparency Report, June 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Good Faith Reading</h3>
              <p className="text-surface-600 text-sm">
                First-time permanent repairs with hot material are best practice — temporary cold-fill patches 
                that crumble within months are a common failure mode elsewhere. If Hertfordshire genuinely fixes 
                90% of urgent potholes permanently first time, that's a real strength, and you should expect the 
                council to document it in any Section 58 defence.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Small Print</h3>
              <p className="text-amber-800 text-sm mb-3">
                Everything in that commitment hinges on the words <strong>"urgent"</strong> and 
                <strong> "identified"</strong>. The report says repairs are prioritised by risk assessment, and 
                that "a shallow pothole on low use rural road will not be such a high priority as a deeper 
                pothole on a main A road."
              </p>
              <p className="text-amber-800 text-sm">
                A defect that hasn't been identified gets no clock at all. A defect assessed below the urgent 
                Cat1 threshold joins the lower-priority queue — the same queue that built up a backlog big 
                enough to need additional funding in 2023/24. And even an identified urgent pothole can lawfully 
                sit in the carriageway for up to four weeks. If your incident falls in any of those gaps, the 
                repair policy doesn't defeat your claim — it frames it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hertfordshire runs a serious asset-management operation — 
            here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — spent £85.2m capital against a £25.7m DfT allocation in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Surveys every road class annually, including all 3,244km of unclassified roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Over 90% of urgent potholes get a first-time permanent hot-material repair
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition stable across six years of machine surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented deterioration modelling and lifecycle planning — roughly 70% of treated area is preventative
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence with inspection records and risk assessments. 
                Generic claims will struggle here.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition and AMBER best practice on the DfT scorecard — not just condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED B roads more than doubled in a year (3.2% → 7.0% in 2024/25)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 18% of U roads RED in 2023/24 — worse than before the £29m improvement programme
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 64% of the network relies on "subjective" visual inspections, by the council's own description
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 155,452 potholes filled in five years — up 49% and still climbing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted backlog of sub-urgent defects and admitted weather damage to "evolved" undesigned roads
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with a GREEN spend record and a professional asset-management operation, 
              your claim lives or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council fault reports) — proof of actual notice</li>
              <li>• Photos showing size, depth and visible age (weathered edges, previous patching, standing water)</li>
              <li>• The road's class — on a B road or unclassified road, the council's own 2024/25 condition data documents the decline</li>
              <li>• Timing — a defect after a wet or freezing spell engages the council's own admission that weather is accelerating deterioration</li>
              <li>• The repair-window question — was your pothole identified, how was it risk-assessed, and where was it in the queue?</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Hertfordshire's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Hertfordshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-run council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No B-road decline data cited</li>
                <li>• No prior-report search</li>
                <li>• No repair-window analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B-road RED doubling documented</li>
                <li>✅ 18% U-road figure and missed £29m target cited</li>
                <li>✅ 155,452 repairs in five years argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Hertfordshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hertfordshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hertfordshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hertfordshire spends three times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN — Hertfordshire spent £85.2m of capital in 2024/25 against 
                a £25.7m DfT allocation. But the overall rating is AMBER because of road condition: RED-condition 
                B roads more than doubled in a year and 18% of unclassified roads needed maintenance at the last 
                published survey. Section 58 turns on whether the specific defect that damaged your vehicle was 
                reasonably inspected and repaired, not on aggregate spending.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on an unclassified or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 3,244km — nearly two-thirds of the Hertfordshire network. At the last 
                published survey (2023/24), 18% were in the RED category, the worst figure since 2016/17 and worse 
                than the 16% start point of the council's own £29m five-year improvement programme. The council 
                also notes U-road figures come from "a more subjective visual inspection" rather than machine surveys.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                RED-condition B roads doubled in a year — what does that mean for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                In 2024/25, B roads in the RED category jumped from 3.2% to 7.0% — the worst figure in the six 
                years of published data — while B roads in good condition fell from 74.4% to 62.0%. If your pothole 
                was on a B road, the council's own machine-survey data documents rapid, recent deterioration across 
                that road class, which is directly relevant to whether its maintenance response was reasonable.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hertfordshire says it fixes urgent potholes within 1 to 4 weeks — does that defeat my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No. That commitment applies only to defects already identified and risk-assessed as urgent 
                Category 1 potholes. The real questions are whether your pothole was identified at all before 
                your incident, whether it was risk-assessed correctly, and how long it sat below the "urgent" 
                threshold. The council's own report says a shallow pothole on a low-use rural road "will not be 
                such a high priority" — and lower-priority defects built up such a backlog that extra funding 
                was allocated in 2023/24 to deal with them.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the "evolved roads" weather admission help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Hertfordshire's transparency report states that much of its unclassified network is "evolved" — 
                historic tracks made up over the years rather than designed roads — and that "the foundations of 
                undesigned roads are much more susceptible to damage if moisture is allowed to penetrate through 
                the surface." That is documented knowledge that a known class of roads deteriorates faster, which 
                raises the bar for what a reasonable inspection and repair regime looks like on those roads under 
                Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did pothole repairs jump 49% in five years?
              </h3>
              <p className="text-surface-600 text-sm">
                Hertfordshire filled 26,037 potholes in 2020/21 and 38,806 in 2024/25 — a 49% increase, averaging 
                106 repairs every day in the latest year. The council explains part of the jump itself: in 2023/24 
                it "allocated additional funding to address more carriageway defects... directed towards lower 
                priority defects that did not meet assessment criteria for an urgent Cat1 pothole repair". In plain 
                terms: there was a known backlog of sub-urgent defects on the network large enough to need its own 
                funding line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a 
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a 
              href="https://www.hertfordshire.gov.uk/doc/roads/plans/local-highway-maintenance-transparency-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hertfordshire County Council Local Highways Maintenance Transparency Report (June 2025)
            </a>. 
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
